import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Form, ListGroup, InputGroup } from "react-bootstrap";
import productHttpService from "../services/http/products/product.service";
import outletStockService from "../services/http/inventory/outlet-stock.service";
import shelfHttpService from "../services/http/shelf-management/shelf.service";
import { useParams } from "react-router-dom";
import usePagination from "../custom-hooks/usePagination";
import ImageWithBasePath from "../core/img/imagewithbasebath";
import UnlimitLoading from "./ui-kit/unlimit-loading";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "react-feather";

// Smart Pagination Component
const SmartPagination = ({ currentPage, totalPages, onPageChange, t }) => {
  const getVisiblePages = useMemo(() => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Always show first page
    range.push(1);

    // Calculate range around current page
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Always show last page if there are multiple pages
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

    // Add dots where there are gaps
    let previous = 0;
    uniqueRange.forEach((page) => {
      if (page - previous === 2) {
        rangeWithDots.push(previous + 1);
      } else if (page - previous !== 1) {
        rangeWithDots.push("...");
      }
      rangeWithDots.push(page);
      previous = page;
    });

    return rangeWithDots;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-center mt-3">
      <nav aria-label="Pagination Navigation">
        <ul className="pagination pagination-sm">
          {/* First Page Button */}
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              aria-label="Go to first page"
              title="First page"
            >
              <ChevronsLeft size={14} />
            </button>
          </li>

          {/* Previous Page Button */}
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
          </li>

          {/* Page Numbers */}
          {getVisiblePages.map((page, index) => {
            if (page === "...") {
              return (
                <li key={`ellipsis-${index}`} className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              );
            }

            return (
              <li
                key={page}
                className={`page-item ${currentPage === page ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(page)}
                  aria-label={`Go to page ${page}`}
                  aria-current={currentPage === page ? "page" : undefined}
                >
                  {page}
                </button>
              </li>
            );
          })}

          {/* Next Page Button */}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </li>

          {/* Last Page Button */}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Go to last page"
              title="Last page"
            >
              <ChevronsRight size={14} />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

const SensorModal = ({
  isOpen,
  closeModal,
  sensorNumber,
  rackNumber,
  rackName,
  shelfNumber,
  saveProductDetails,
}) => {
  const [products, setProducts] = useState([]);
  const [allSelectedProducts, setAllSelectedProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  const [preselectedProductsLoaded, setPreselectedProductsLoaded] =
    useState(false);
  const { id: outletId } = useParams();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({
    nameFilter: "",
    lmProductNumberFilter: "",
  });

  const [existingProductParams, setExistingProductParams] = useState({
    nameFilter: "",
    lmProductNumberFilter: "",
  });
  const { paginationParams, updatePaginationParams } = usePagination({
    currentPage: 1,
    productsPerPage: 10,
  });

  const { currentPage, productsPerPage } = paginationParams;
  const totalPages = Math.ceil(totalProducts / productsPerPage);

  // Separate function to fetch preselected products
  const fetchPreselectedProducts = async () => {
    try {
      console.log("Fetching preselected products for:", {
        outletId,
        rackNumber,
        shelfNumber,
        sensorNumber,
      });

      const preselectedResponse = await shelfHttpService.getSaveProduct(
        outletId
      );

      if (preselectedResponse?.status !== 200) {
        console.warn("Failed to fetch preselected products");
        return [];
      }

      const preselectedList = preselectedResponse.data.data || [];
      console.log("All preselected data:", preselectedList);

      // More robust filtering with better logging
      const matchingRack = preselectedList.find((rack) => {
        const rackIdMatch = rack?.id?.toString() === rackNumber?.toString();
        console.log(
          `Rack comparison: ${rack?.id} === ${rackNumber} -> ${rackIdMatch}`
        );
        return rackIdMatch;
      });

      if (!matchingRack) {
        console.log("No matching rack found");
        return [];
      }

      console.log("Found matching rack:", matchingRack);

      const matchingShelf = matchingRack.shelves?.find((shelf) => {
        const shelfMatch = shelf.name?.toString() === shelfNumber?.toString();
        console.log(
          `Shelf comparison: ${shelf.name} === ${shelfNumber} -> ${shelfMatch}`
        );
        return shelfMatch;
      });

      if (!matchingShelf) {
        console.log("No matching shelf found");
        return [];
      }

      console.log("Found matching shelf:", matchingShelf);

      const matchingSensor = matchingShelf.sensors?.find((sensor) => {
        const sensorMatch =
          sensor.sensor_number?.toString() === sensorNumber?.toString();
        console.log(
          `Sensor comparison: ${sensor.sensor_number} === ${sensorNumber} -> ${sensorMatch}`
        );
        return sensorMatch;
      });

      if (!matchingSensor) {
        console.log("No matching sensor found");
        return [];
      }

      console.log("Found matching sensor:", matchingSensor);
      const preselectedProducts = matchingSensor.products || [];
      console.log("Preselected products:", preselectedProducts);

      return preselectedProducts;
    } catch (error) {
      console.error("Error fetching preselected products:", error);
      return [];
    }
  };

  // Load preselected products only once when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadPreselectedProducts = async () => {
      setLoading(true);
      try {
        const preselectedProducts = await fetchPreselectedProducts();

        if (preselectedProducts.length > 0) {
          // Set selected products
          setAllSelectedProducts(preselectedProducts);
          setFilteredProducts(preselectedProducts);
          // Set initial quantities
          const initialQuantities = {};
          preselectedProducts.forEach((product) => {
            initialQuantities[product.id] = product.quantity || 0;
          });
          setQuantities(initialQuantities);

          console.log("Loaded preselected products:", preselectedProducts);
          console.log("Set initial quantities:", initialQuantities);
        }

        setPreselectedProductsLoaded(true);
      } catch (error) {
        console.error("Error loading preselected products:", error);
        setError("Failed to load existing products. Please try again.");
        setPreselectedProductsLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    // Reset states when modal opens
    setAllSelectedProducts([]);
    setFilteredProducts([]);
    setQuantities({});
    setPreselectedProductsLoaded(false);
    setError(null);

    loadPreselectedProducts();
  }, [isOpen, sensorNumber, rackNumber, shelfNumber, outletId]);

  // Fetch products list with pagination and search
  useEffect(() => {
    if (!isOpen || !preselectedProductsLoaded) return;

    const fetchProductsList = async () => {
      setLoading(true);
      try {
        const productResponse = await outletStockService.shopProducts(
          outletId,
          {
            page: currentPage,
            per_page: productsPerPage,
            name: searchParams.nameFilter,
            ean: searchParams.lmProductNumberFilter,
            lm_product_number: searchParams.lmProductNumberFilter,
          }
        );

        if (productResponse?.status === 200) {
          const productsList = productResponse.data.data.data || [];
          setTotalProducts(productResponse.data.data.total || 0);

          // Filter out already selected products from the products list
          const filteredProducts = productsList.filter(
            (product) =>
              !allSelectedProducts.some(
                (selected) => selected.id === product.id
              )
          );

          setProducts(filteredProducts);
          console.log("Loaded products list:", productsList.length);
          console.log(
            "Filtered products (excluding selected):",
            filteredProducts.length
          );
        } else {
          setProducts([]);
          setTotalProducts(0);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductsList();
  }, [
    isOpen,
    preselectedProductsLoaded,
    allSelectedProducts,
    currentPage,
    productsPerPage,
    searchParams,
    outletId,
  ]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setProducts([]);
      setAllSelectedProducts([]);
      setFilteredProducts([]);
      setQuantities({});
      setPreselectedProductsLoaded(false);
      setError(null);
      setSearchParams({ nameFilter: "", lmProductNumberFilter: "" });
      // Reset pagination when modal closes
      updatePaginationParams({
        currentPage: 1,
        nameFilter: "",
        lmProductNumberFilter: "",
      });
    }
  }, [isOpen, updatePaginationParams]);

  const toggleProductSelection = (product) => {
    if (allSelectedProducts.some((p) => p.id === product.id)) {
      // Remove from selected
      setAllSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
      setFilteredProducts((prev) => prev.filter((p) => p.id !== product.id));
      setProducts((prev) => [...prev, product]);
      // Remove quantity when deselecting
      setQuantities((prev) => {
        const updated = { ...prev };
        delete updated[product.id];
        return updated;
      });
    } else {
      // Add to selected
      setAllSelectedProducts((prev) => [...prev, product]);
      setFilteredProducts((prev) => [...prev, product]);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      // Set default quantity
      setQuantities((prev) => ({
        ...prev,
        [product.id]: 0,
      }));
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    const validQuantity = Math.max(0, parseInt(quantity) || 0);
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productId]: validQuantity,
    }));
  };

  const handleExistingProductChange = (e, key) => {
    const { value } = e.target;
    setExistingProductParams((prev) => ({
      ...prev,
      [key]: value,
    }));
    console.log("Existing product params updated:", existingProductParams);
  };

  const handleExistingProductSearch = async () => {
    console.log(
      "Searching existing products with params:",
      existingProductParams
    );
    setFilteredProducts((prev) => {
      return allSelectedProducts.filter((product) => {
        return (
          product.name
            .toLowerCase()
            .includes(existingProductParams.nameFilter.toLowerCase()) &&
          (product.lm_product_number
            .toLowerCase()
            .includes(
              existingProductParams.lmProductNumberFilter.toLowerCase()
            ) ||
            product.ean
              .toLowerCase()
              .includes(
                existingProductParams.lmProductNumberFilter.toLowerCase()
              ))
        );
      });
    });
  };

  const handleSearch = () => {
    setSearchParams({
      nameFilter: paginationParams.nameFilter || "",
      lmProductNumberFilter: paginationParams.lmProductNumberFilter || "",
    });
    // Reset to first page when searching
    updatePaginationParams({ currentPage: 1 });
  };

  const handleSave = () => {
    const selectedWithQuantities = allSelectedProducts.map((product) => ({
      ...product,
      quantity: quantities[product.id] || 0,
    }));

    console.log("Saving products:", selectedWithQuantities);
    saveProductDetails(sensorNumber, selectedWithQuantities);
    closeModal();
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      updatePaginationParams({ currentPage: pageNumber });
    }
  };

  const handleSearchChange = (e) => {
    updatePaginationParams({ nameFilter: e.target.value });
  };

  const handleEanChange = (e) => {
    updatePaginationParams({ lmProductNumberFilter: e.target.value });
  };

  // Handle Enter key for search
  const handleKeyPress = (e, type = "backend") => {
    if (e.key === "Enter") {
      if (type == "backend") {
        handleSearch();
      } else {
        handleExistingProductChange(e);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Modal show={isOpen} onHide={closeModal} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {rackName} <ArrowRight size={16} className="mx-1" /> {shelfNumber}{" "}
          <ArrowRight size={16} className="mx-1" />
          <span className="sensor-modal-title">Sensor {sensorNumber}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Loading state for initial load */}
        {loading && !preselectedProductsLoaded && (
          <div className="d-flex flex-column align-items-center py-4">
            <UnlimitLoading
              type="spin"
              color="#9A0000"
              height={50}
              width={50}
            />
            <span className="ms-2">Loading existing products...</span>
          </div>
        )}

        {/* HORIZONTAL LAYOUT - Row with two columns */}
        {preselectedProductsLoaded && (
          <div className="row">
            {/* LEFT COLUMN - Selected Products Section */}
            <div className="col-md-6">
              <div className="border-end pe-3">
                <div className="fw-bold mb-2 text-success">
                  {t("shelf.selectedProducts")}
                  {allSelectedProducts.length > 0 && (
                    <span className="badge bg-success ms-2">
                      {allSelectedProducts.length}
                    </span>
                  )}
                </div>
                <InputGroup className="mb-3">
                  <Form.Control
                    placeholder={t("tableColumnName.productName")}
                    value={existingProductParams.nameFilter || ""}
                    onChange={(e) =>
                      handleExistingProductChange(e, "nameFilter")
                    }
                  />
                  <Form.Control
                    placeholder="LM Product Number or EAN"
                    value={existingProductParams.lmProductNumberFilter || ""}
                    onChange={(e) =>
                      handleExistingProductChange(e, "lmProductNumberFilter")
                    }
                  />
                  <Button
                    variant="secondary"
                    onClick={() => handleExistingProductSearch()}
                    disabled={loading}
                  >
                    <Search size={16} />
                  </Button>
                </InputGroup>

                {filteredProducts.length === 0 ? (
                  <div className="text-muted p-3 border rounded">
                    {t("shelf.noProductsSelected")}
                  </div>
                ) : (
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <ListGroup>
                      {filteredProducts.map((product) => (
                        <ListGroup.Item
                          key={product.id}
                          className="d-flex justify-content-between align-items-center bg-light"
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            <Form.Check
                              className="me-2"
                              type="checkbox"
                              checked={true}
                              onChange={() => toggleProductSelection(product)}
                            />
                            {product?.main_product_image ? (
                              <ImageWithBasePath
                                type="link"
                                src={product?.main_product_image}
                                alt="Product Image"
                                style={{
                                  width: 30,
                                  height: 30,
                                  marginRight: 10,
                                  objectFit: "cover",
                                }}
                                className="rounded"
                              />
                            ) : (
                              <div
                                className="bg-secondary rounded d-flex align-items-center justify-content-center me-2"
                                style={{ width: 30, height: 30, fontSize: 10 }}
                              >
                                {t("shelf.noImage")}
                              </div>
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-medium">
                                {product?.name || "Unnamed Product"}
                              </div>
                              <small className="text-muted">
                                LM No: {product?.lm_product_number || "N/A"}
                                {", "}
                                EAN: {product?.ean || "N/A"} (
                                {product?.actual_zemic_sensor_weight}g)
                              </small>
                            </div>
                          </div>
                          <InputGroup style={{ width: "100px" }}>
                            <Form.Control
                              type="number"
                              min="0"
                              size="sm"
                              value={quantities[product.id] || ""}
                              onChange={(e) =>
                                handleQuantityChange(product.id, e.target.value)
                              }
                              placeholder="0"
                            />
                          </InputGroup>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Available Products Section */}
            <div className="col-md-6">
              <div className="ps-3">
                {/* Search and Product List Section */}
                <div className="fw-bold mb-2 text-primary">
                  {t("shelf.searchProduct")}
                </div>
                <InputGroup className="mb-3">
                  <Form.Control
                    placeholder={t("tableColumnName.productName")}
                    value={paginationParams.nameFilter || ""}
                    onChange={handleSearchChange}
                    onKeyPress={handleKeyPress}
                  />
                  <Form.Control
                    placeholder="LM Product Number or EAN"
                    value={paginationParams.lmProductNumberFilter || ""}
                    onChange={handleEanChange}
                    onKeyPress={handleKeyPress}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    <Search size={16} />
                  </Button>
                </InputGroup>

                {loading && preselectedProductsLoaded ? (
                  <div className="d-flex justify-content-center py-4">
                    <UnlimitLoading
                      type="spin"
                      color="#9A0000"
                      height={50}
                      width={50}
                    />
                  </div>
                ) : products.length === 0 && !loading ? (
                  <div className="text-center py-4">
                    <div className="text-muted">
                      {searchParams.nameFilter ||
                      searchParams.lmProductNumberFilter
                        ? t("shelf.noProductsFoundForSearch")
                        : t("shelf.noProductsFound")}
                    </div>
                    {(searchParams.nameFilter ||
                      searchParams.lmProductNumberFilter) && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setSearchParams({
                            nameFilter: "",
                            lmProductNumberFilter: "",
                          });
                          updatePaginationParams({
                            nameFilter: "",
                            lmProductNumberFilter: "",
                            currentPage: 1,
                          });
                        }}
                      >
                        {t("general.button.clearSearch")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Products Header */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-bold">{t("shelf.products")}</div>
                      {/*  <div className="fw-bold">{t("shelf.quantity")}</div> */}
                    </div>

                    {/* Products List */}
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <ListGroup>
                        {products.map((product) => (
                          <ListGroup.Item
                            key={product.id}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <Form.Check
                                className="me-2"
                                type="checkbox"
                                checked={false}
                                onChange={() => toggleProductSelection(product)}
                              />
                              {product?.main_product_image ? (
                                <ImageWithBasePath
                                  type="link"
                                  src={product?.main_product_image}
                                  alt="Product Image"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 10,
                                    objectFit: "cover",
                                  }}
                                  className="rounded"
                                />
                              ) : (
                                <div
                                  className="bg-secondary rounded d-flex align-items-center justify-content-center me-2"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    fontSize: 10,
                                  }}
                                >
                                  {t("shelf.noImage")}
                                </div>
                              )}
                              <div className="flex-grow-1">
                                <div className="fw-medium">
                                  {product?.name || "Unnamed Product"}
                                </div>
                                <small className="text-muted">
                                  LM No: {product?.lm_product_number || "N/A"}
                                  {", "}
                                  EAN: {product?.ean || "N/A"} (
                                  {product?.actual_zemic_sensor_weight}g)
                                </small>
                              </div>
                            </div>
                            {/*  <InputGroup style={{ width: "100px" }}>
                              <Form.Control
                                type="number"
                                min="0"
                                size="sm"
                                value={quantities[product.id] || ""}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    product.id,
                                    e.target.value
                                  )
                                }
                                placeholder="0"
                              />
                            </InputGroup> */}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  </>
                )}
              </div>
              <SmartPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                t={t}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          {t("shelf.close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={allSelectedProducts.length === 0 || loading}
        >
          {t("general.button.add")} ({allSelectedProducts.length})
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SensorModal;
